/**
 * ActionDialog — the confirm step for a platform-admin write action. The
 * admin must type a reason. The reason goes to the audit log.
 */
import { useState, type ReactNode } from 'react';
import { AlertDialog, Button, Callout, Flex, Text, TextArea } from '@radix-ui/themes';

interface Props {
  trigger: ReactNode;
  title: string;
  description: string;
  confirmLabel: string;
  color?: 'red' | undefined;
  children?: ReactNode;
  // Return a message to show, or nothing on success.
  onConfirm: (reason: string) => Promise<string | void>;
}

const ActionDialog: React.FC<Props> = ({ trigger, title, description, confirmLabel, color, children, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = reason.trim().length >= 3;

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const msg = await onConfirm(reason.trim());
      if (msg) {
        setError(msg);
      } else {
        setOpen(false);
        setReason('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setError(null); }}>
      <AlertDialog.Trigger>{trigger}</AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="520px">
        <AlertDialog.Title>{title}</AlertDialog.Title>
        <AlertDialog.Description size="2" color="gray">{description}</AlertDialog.Description>
        <Flex direction="column" gap="3" mt="3">
          {children}
          <label>
            <Text as="div" size="2" weight="medium" mb="1">Reason (required, saved to the audit log)</Text>
            <TextArea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you making this change?" />
          </label>
          {error && <Callout.Root color="red"><Callout.Text>{error}</Callout.Text></Callout.Root>}
        </Flex>
        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray">Cancel</Button>
          </AlertDialog.Cancel>
          <Button color={color} disabled={!valid || busy} onClick={confirm}>{busy ? 'Saving…' : confirmLabel}</Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};

export default ActionDialog;
